// The rendered form owns its dirty check. Browser history uses that same close
// request instead of bypassing it through the parent's state setter.
//
// Elke Modal (components/HomeV2/Modal.js) staat hier, ook zonder historyKey: dan
// onder een eigen sleutel. Dit is het ENE register van open modals met hun
// sluitvraag; useDraftGuard(reloadOnly) in lib.js telt alleen voor herladen.
const requests = new Map();

export function registerModalClose(key, guard) {
  if (!key) return () => {};
  requests.set(key, guard);
  return () => { if (requests.get(key) === guard) requests.delete(key); };
}

// De bovenste (laatst geopende) modal met niet-opgeslagen invoer, of null.
function topDirty({ askable = false } = {}) {
  let top = null;
  for (const guard of requests.values()) {
    if (askable && typeof guard.confirmDiscard !== 'function') continue;
    try { if (guard.isDirty()) top = guard; } catch { /* een kapotte check telt niet */ }
  }
  return top;
}

export function closeModalFromHistory(key, restore, close) {
  // Geen eigen guard onder deze sleutel (de flow zet zijn Modal zonder historyKey,
  // bv. CV versturen in de CRM): dan vraagt de bovenste vuile modal. Anders sloot
  // Back die modal stil en was de invoer weg (6 okt 2026).
  // Net bevestigd via de navigatiewacht hieronder: de popstate van die navigatie
  // (een hashwissel vuurt er ook een) is dan geen nieuwe vraag waard.
  const guard = requests.has(key) ? requests.get(key) : topDirty();
  if (guard?.isDirty() && Date.now() >= approvedUntil) {
    // Restore before asking: Cancel keeps the form AND its history entry.
    // A confirmed close lets the normal hook cleanup consume that entry once.
    restore();
    guard.requestClose();
    return false;
  }
  close();
  return true;
}

// ── Navigatie langs een vuile modal (6 okt 2026) ─────────────────────────────
// Een andere hash (recordlink, kruimel, Back naar een ander record, code die
// location.hash zet) of een direct geopend ander record (⌘K, belkaart) ruimt de
// modal op zonder zijn sluitvraag: de selectie in bv. CV versturen was stil weg.
// Elke zulke navigatie vraagt hier eerst, met de sluitvraag van de modal zelf.
//
// Eerst even wachten: een modal die in DEZELFDE klik sluit (opslaan en daarna het
// nieuwe record openen) is na de commit niet meer vuil en mag stil door. Het
// akkoord geldt kort voor de rest van dezelfde navigatie (hashchange-handler en
// recordsetter vragen dan niet nog eens).
export const NAV_SETTLE_MS = 60;
const APPROVAL_MS = 1500;
let approvedUntil = 0;
let pending = null;

function wait(ms) { return new Promise((resolve) => setTimeout(resolve, ms)); }

// true: de aanroeper mag nu door. false: de navigatie is tegengehouden; proceed()
// draait later, alleen als de modal intussen dicht is of de gebruiker bevestigt.
export function guardModalNavigation(proceed) {
  if (Date.now() < approvedUntil || !topDirty({ askable: true })) return true;
  if (!pending) {
    pending = (async () => {
      try {
        await wait(NAV_SETTLE_MS);
        const guard = topDirty({ askable: true });
        const ok = !guard || await guard.confirmDiscard();
        if (ok) approvedUntil = Date.now() + APPROVAL_MS;
        return ok;
      } catch { return false; } finally { pending = null; }
    })();
  }
  pending.then((ok) => { if (ok) proceed(); });
  return false;
}

// De ene centrale wacht op hash-navigatie, via de Navigation API: die meldt een
// navigatie VOOR de URL wisselt en laat hem annuleren. Annuleren laat de URL en de
// history dus ongemoeid (geen spookentry); na akkoord loopt dezelfde navigatie
// opnieuw. pushState/replaceState van de app zelf (hashChange false) en de
// modal-entry van useModalHistory (zelfde URL) gaan er buiten om.
export function installModalNavigationGuard(nav = typeof window !== 'undefined' ? window.navigation : null) {
  if (!nav || typeof nav.addEventListener !== 'function') return () => {};
  const onNavigate = (e) => {
    if (!e.hashChange || !e.cancelable) return;
    const type = e.navigationType;
    if (type !== 'push' && type !== 'replace' && type !== 'traverse') return;
    const url = e.destination?.url;
    const key = e.destination?.key;
    const replay = () => {
      try {
        const r = type === 'traverse' ? nav.traverseTo(key) : nav.navigate(url, { history: type });
        r?.committed?.catch?.(() => {}); r?.finished?.catch?.(() => {});
      } catch { /* entry bestaat niet meer: niets te doen */ }
    };
    if (guardModalNavigation(replay)) return;
    e.preventDefault();
  };
  nav.addEventListener('navigate', onNavigate);
  return () => nav.removeEventListener('navigate', onNavigate);
}

// Alleen voor tests.
export function _resetModalNavigation() { approvedUntil = 0; pending = null; }
